import { useEffect } from "react";
import { useRoute } from "@/components/shared/router";
import { Button, View } from "@/components/ui";
import { ensurePhoneNumberWithPlus } from "@/lib/helpers/string";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { useAuthState } from "@/store/auth";
import { useClientVerifyPhoneNumberStore } from "@/store/auth/clientVerifyPhoneNumber";
import { useSignUpForm } from "@/store/auth/forms/useSignUpForm";
import { AuthModals, LoginType } from "@/types/users";

import { toast } from "@luce/ui";

import { TextSeparator } from "../../text-separator/TextSeparator";
import { Typography } from "../../typography";
import { SignUpForm } from "../sign-up-modal/sign-up-form";

export const SignUpContent = ({ isModal = true }: { isModal?: boolean }) => {
  const { replace } = useRoute();
  const formReturn = useSignUpForm();
  const { reset, defaultValues } = formReturn;
  const { loading, fetch: verifyPhoneNumber, error } = useClientVerifyPhoneNumberStore();

  const { setPhone, setType, showModal, setLoginByModal, setSignUpFormReturn } = useAuthState();

  useEffect(() => {
    setLoginByModal(isModal);
    setSignUpFormReturn(formReturn);
  }, []);

  const onSubmitForm = (data: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
  }) => {
    setType(LoginType.SignUp);
    const phone = ensurePhoneNumberWithPlus(data.phoneNumber);
    setPhone(phone);
    reset(data);
    verifyPhoneNumber(
      {
        requestPayload: {
          input: {
            phone: phone,
            onSignUp: true,
            enableWhatsappOtp: true,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      if (res.error === "Phone number is already registered") {
        toast.info("You have already have an account, please try to login");
        setType(LoginType.Login);
        onGoToLogin();
        return;
      }
      PosthogAnalytics.track("user_sign_up_send_otp");
      if (res.error && !res.error?.includes("Last pin code is still active")) return;
      showModal(AuthModals.OTP);
    });
  };
  const onGoToLogin = () => {
    reset(defaultValues);
    if (isModal) showModal(AuthModals.LOGIN);
    else replace({ pageKey: "login" });
  };

  return (
    <View className="flex flex-col gap-2">
      <View className="flex flex-col gap-1 pb-3">
        <Typography variant="label-md">Sign Up</Typography>
        <Typography variant="body-sm" color="foreground-intermediate">
          To continue the booking process, you need to login or create a new account
        </Typography>
      </View>
      <SignUpForm
        signUpFormReturn={formReturn}
        onSubmitForm={onSubmitForm}
        errorMessage={error || ""}
        loading={loading}
      />
      <TextSeparator label="Have an account already?" />
      <Button
        variant="secondary"
        color="CTA2"
        rounded="full"
        className="mt-4"
        onClick={onGoToLogin}
        children="Sign In"
      />
    </View>
  );
};
